/**
 * DeleteConfirmModal Component
 *
 * Confirmation dialog for deleting favorites lists.
 * Shows warning and requires explicit confirmation.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../base/Modal';
import Button from '../base/Button';

export interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  listName: string;
}

export default function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  listName,
}: DeleteConfirmModalProps) {
  const { t, i18n } = useTranslation('favorites');
  const isRTL = i18n.dir() === 'rtl';
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Failed to delete list:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
    >
      <div className="text-center">
        {/* Warning Icon */}
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
          <i className="ri-delete-bin-line text-3xl text-red-500"></i>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {t('favorites.lists.deleteList')}
        </h3>

        {/* Confirmation Message */}
        <p className="text-gray-600 mb-2">
          {t('favorites.lists.deleteConfirm', { name: listName })}
        </p>

        {/* Warning about properties being moved */}
        <p className="text-sm text-gray-500 mb-6">
          {t('favorites.lists.deleteWarning')}
        </p>
      </div>

      {/* Actions */}
      <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button
          variant="outline"
          onClick={onClose}
          className="flex-1"
          disabled={isDeleting}
        >
          {t('favorites.actions.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={isDeleting}
          className="flex-1 bg-red-500 hover:bg-red-600 focus:ring-red-500"
        >
          {isDeleting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              {t('favorites.loading')}
            </span>
          ) : (
            t('favorites.actions.delete')
          )}
        </Button>
      </div>
    </Modal>
  );
}
